'use client'

import { isEmpty, isNil } from 'lodash-es'
import { useTranslations } from 'next-intl'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'
import {
  toast,
  ToastContainer,
  ToastContainerProps,
  Zoom,
} from 'react-toastify'

import { TSearchRoom, useMapStore } from '@/entities/map'
import { cn } from '@/shared/lib'
import { CenteredToastStyle, DefaultToastStyle } from '@/shared/ui'

export const MAP_TOAST_CONTAINER_ID = 'MAP'

type Props = ToastContainerProps

type MapToastState = {
  showToast: (message: string) => void
}

const MapToastContext = createContext<MapToastState>({
  showToast: () => {},
})

export const MapToastProvider = ({
  children,
  ...restProps
}: PropsWithChildren<Props>) => {
  const showToast = useCallback((message: string) => {
    toast.dismiss({
      containerId: MAP_TOAST_CONTAINER_ID,
    })

    toast(message, {
      containerId: MAP_TOAST_CONTAINER_ID,
    })
  }, [])

  const value = useMemo(() => ({ showToast }), [showToast])

  return (
    <MapToastContext.Provider value={value}>
      <ToastContainer
        autoClose={1000}
        transition={Zoom}
        toastClassName={cn(
          DefaultToastStyle,
          CenteredToastStyle,
          'top-auto bottom-[70px] translate-y-0',
        )}
        closeButton={false}
        hideProgressBar
        containerId={MAP_TOAST_CONTAINER_ID}
        draggable
        draggableDirection="y"
        {...restProps}
      />
      {children}
    </MapToastContext.Provider>
  )
}

type UseToastProps = {
  isMounted: boolean
  rooms: TSearchRoom[] | null
  isFetching: boolean
}

export const useMapToast = ({
  isMounted,
  rooms,
  isFetching,
}: UseToastProps) => {
  const t = useTranslations('map')

  const { showToast } = useContext(MapToastContext)

  const canFetchRoom = useMapStore(state => state.canFetchRoom)
  const zoomLevel = useMapStore(state => state.params.zoomLevel)

  useEffect(() => {
    if (!canFetchRoom()) {
      showToast(t('list.zoom_message'))
    }
  }, [showToast, zoomLevel, t])

  useEffect(() => {
    if (!isMounted || isNil(rooms)) return

    if (!isFetching && isEmpty(rooms)) {
      showToast(t('list.no_result_message'))
    }
  }, [isFetching, t])
}
